import { datadogRum } from '@datadog/browser-rum';
import { HttpStatusError } from 'src/domain/errors/HttpStatusError';
import { isErrorResponse } from 'src/utils/api/error-utils';
import { getToken } from 'src/utils/token/tokenUtils';

async function throwFileTransferError(response: Response): Promise<never> {
    if (response.status === 401) {
        throw new HttpStatusError(response.status, response.statusText, {
            message: 'Your session has expired, please reload the page and try again',
            errorType: 'InsufficientPermissions',
            statusCode: response.status,
        });
    }

    const body: unknown = await response
        .clone()
        .json()
        .catch(() => response.text());
    throw new HttpStatusError(
        response.status,
        response.statusText,
        isErrorResponse(body) || typeof body === 'string' ? body : undefined
    );
}

type HttpDownloadMethod = 'GET' | 'POST';

export function handleDownload<TBody>(filename: string, url: string, method: HttpDownloadMethod, body?: TBody) {
    return fetch(url, {
        method,
        headers: {
            Authorization: 'Bearer ' + getToken(),
            'Content-Type': 'application/json',
        },
        body: body ? JSON.stringify(body) : undefined,
    }).then((res) => {
        if (res.ok) {
            return res.blob().then((blob) => {
                const objectUrl = globalThis.URL.createObjectURL(blob);

                const a = document.createElement('a');
                a.href = objectUrl;
                a.download = filename ?? 'download.txt';
                a.click();
            });
        } else {
            return throwFileTransferError(res);
        }
    });
}

type HttpUploadMethod = 'POST' | 'PUT';

export async function handleUpload(url: string, method: HttpUploadMethod, body: FormData): Promise<void> {
    try {
        const response = await fetch(url, {
            method,
            headers: {
                Authorization: 'Bearer ' + getToken(),
            },
            body,
        });
        if (!response.ok) {
            await throwFileTransferError(response);
        }
    } catch (error: unknown) {
        datadogRum.addError(error, { operation: 'upload', method });
        throw error;
    }
}
